import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component } from '@angular/core';

import { environmentOm } from '../../environment-om';
import { ReportsApi } from '../api/reports-api';

import { ReportHelper } from './report-helper';
import { ReportLauncher } from './report-launcher';

@Component({
    selector: 'om-report-list',
    providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }],
    templateUrl: './report-list.component.html',
    styleUrls: ['./report-list.component.css']
})

export class ReportListComponent implements AfterViewInit {

    constructor(
        private readonly reportHelper: ReportHelper,
        private readonly location: Location,
        private readonly reportsApi: ReportsApi,
        private readonly reportLauncher: ReportLauncher,
        private readonly changeDectRef: ChangeDetectorRef
    ) { }

    // all available report data loaded on startup
    public reportData: any;

    // supplied for csv reports
    public reportName: string;

    private searchCriteriaRegion: any;

    // reports shown in list
    public reportsToShow: IReport[] = [];

    // project totals shown in list
    private projectTotals: any;

    public loading = { status: false, text: 'Loading...' };
    // some reports need need asset ID's from a table column, some need a region value(s) from the AOI. Leave unused values unassigned.
    public reports: IReport[] = [
        {
            name: 'projectListReport',
            title: 'Project List Report',
            reportType: 'projects',
            data: ['projectIds'],
            tables: this.reportHelper.tableNames.mpms,
            csvPrint: true,
            disabled: false
        },
        {
            name: 'municipalityAssetTipReport',
            title: 'Municipality Asset TIP Report',
            reportType: 'tip',
            data: ['countyMunicipalityCode'],
            tables: this.reportHelper.tableNames.mpms,
            csvPrint: false,
            disabled: false
        },
        {
            name: 'municipalityAssetDraftTipReport',
            title: 'Municipality Asset Draft TIP Report',
            reportType: 'draft',
            data: ['countyMunicipalityCode'],
            tables: this.reportHelper.tableNames.mpms,
            csvPrint: false,
            disabled: false
        },
        {
            name: 'legislativeAssetTipReport',
            title: 'Legislative Asset TIP Report',
            reportType: 'tip',
            data: ['houseDistrict', 'senateDistrict', 'congressDistrict'],
            disabled: false,
            csvPrint: false,
            tables: this.reportHelper.tableNames.mpms
        },
        {
            name: 'legislativeAssetDraftTipReport',
            title: 'Legislative Asset Draft TIP Report',
            reportType: 'draft',
            data: ['houseDistrict', 'senateDistrict', 'congressDistrict'],
            disabled: false,
            csvPrint: false,
            tables: this.reportHelper.tableNames.mpms
        },
        {
            name: 'sdBridgeReport',
            title: 'Poor Bridge Condition Report',
            reportType: 'sd',
            data: ['bridgeKeys'],
            disabled: false,
            csvPrint: true,
            tables: this.reportHelper.tableNames.bridges
        },
        {
            name: 'sdBridgeSummaryReport',
            title: 'Poor Bridge Condition Summary Report',
            reportType: 'sd',
            data: ['bridgeKeys'],
            disabled: false,
            csvPrint: false,
            tables: this.reportHelper.tableNames.bridges
        },
        {
            name: 'bridgeDataReport',
            title: 'Bridge Data Report',
            reportType: 'bridges',
            data: ['bridgeKeys'],
            disabled: false,
            csvPrint: true,
            tables: this.reportHelper.tableNames.bridges
        },
        {
            name: 'planningStatsTipReport',
            title: 'Planning Stats TIP Report',
            reportType: 'tip',
            data: ['planningPartner'],
            disabled: false,
            csvPrint: false,
            tables: this.reportHelper.tableNames.mpms
        },
        {
            name: 'planningStatsDraftTipReport',
            title: 'Planning Stats Draft TIP Report',
            reportType: 'draft',
            data: ['planningPartner'],
            disabled: false,
            csvPrint: false,
            tables: this.reportHelper.tableNames.mpms
        },
        {
            name: 'opiProjectEvaluationTipReport',
            title: 'OPI Project Evaluation TIP Report',
            reportType: 'tip',
            data: [
                'projectIds',
                'county',
                'district',
                'countyMunicipalityCode',
                'congressDistrict',
                'senateDistrict',
                'houseDistrict',
                'planningPartner'
            ],
            disabled: false,
            csvPrint: true,
            tables: this.reportHelper.tableNames.mpms
        },
        {
            name: 'opiProjectEvaluationDraftTipReport',
            title: 'OPI Project Evaluation Draft TIP Report',
            reportType: 'draft',
            data: [
                'projectIds',
                'county',
                'district',
                'countyMunicipalityCode',
                'congressDistrict',
                'senateDistrict',
                'houseDistrict',
                'planningPartner'
            ],
            disabled: false,
            csvPrint: true,
            tables: this.reportHelper.tableNames.mpms
        },
        {
            name: 'iriProjectEvaluationTipReport',
            title: 'IRI Project Evaluation TIP Report',
            reportType: 'tip',
            data: [
                'projectIds',
                'county',
                'district',
                'countyMunicipalityCode',
                'congressDistrict',
                'senateDistrict',
                'houseDistrict',
                'planningPartner'
            ],
            disabled: false,
            csvPrint: true,
            tables: this.reportHelper.tableNames.mpms
        },
        {
            name: 'iriProjectEvaluationDraftTipReport',
            title: 'IRI Project Evaluation Draft TIP Report',
            reportType: 'draft',
            data: [
                'projectIds',
                'county',
                'district',
                'countyMunicipalityCode',
                'congressDistrict',
                'senateDistrict',
                'houseDistrict',
                'planningPartner'
            ],
            disabled: false,
            csvPrint: true,
            tables: this.reportHelper.tableNames.mpms
        }
    ];

    public csvReport: ICsvReport = {
        name: 'csvReport',
        data: [
            'projectIds',
            'bridgeKeys',
            'county',
            'district',
            'countyMunicipalityCode',
            'congressDistrict',
            'senateDistrict',
            'houseDistrict',
            'planningPartner'
        ]
    };

    ngAfterViewInit() {
        const window = document.getElementById('reportsListWindow');
        if (window) {
            window.addEventListener('openChange', (open: any) => {
                if (open.detail) {
                    this.windowOpened();
                }
            });
        } else {
            this.windowOpened();
        }

        this.changeDectRef.detectChanges();
    }

    async windowOpened() {
        this.loading = { status: true, text: 'Loading...' };
        this.reportHelper.setMapRef();
        // get the search criteria from mapcore
        this.searchCriteriaRegion = this.getSearchCriteria();

        // load all project/bridge ids available on map and assign selected regions to report parameters
        this.reportData = await this.loadData();
        // get total numbers of TIP or Draft projects or poor condition bridges to display with report names
        await this.getProjectTotals(this.reportData);
        // decide what reports to show in list based on selected regions/maps etc
        this.determineVisibleReports();
        this.loading.status = false;

    }

    public getSearchCriteria() {
        return this.reportHelper.mapRef.AoiService.getSearchCriterias();
    }

    public determineVisibleReports() {
        // set the report list to empty
        this.reportsToShow = [];
        const activeTables: string[] = this.getActiveTables();

        // evaluate each report, determine if it should be visible based on the active layers
        for (const report of this.reports) {

            /*
            *Check and see if the report requires both project Ids/bridge keys and a region variable. If it does we
            *need to create a new temporary data array without project Id's or bridge keys. If someone adds a layer to the map
            *outside of the AOI there are no search criteria regions for the report to generate data but it will still make the list.
            */
            const tempReportData = [];
            for (const item of report.data) {
                if (item.indexOf('projectIds') > -1 || item.indexOf('bridgeKeys') > -1) {
                    continue;
                } else {
                    tempReportData.push(item);
                }
            }

            this.setupReportsToShow(report, activeTables, tempReportData);
        }
    }

    getActiveTables(): string[] {
        const activeTables: string[] = [];
        const layers = this.reportHelper.mapLayers && this.reportHelper.mapLayers.length > 0 ? this.reportHelper.mapLayers : [];

        for (const layer of layers) {
            if (layer.visible === true) {
                // layer.layerDefs contains the list of all fully qualified tables that this layer uses.
                layer.mapcoreProps.tableNames.forEach(fullyQualifiedTableName => {
                    if (activeTables.indexOf(fullyQualifiedTableName) < 0) {
                        activeTables.push(fullyQualifiedTableName);
                    }
                });
            }
        }

        return activeTables;
    }

    setupReportsToShow(report: IReport, activeTables: string[], tempReportData: any[]) {
        // look at each active table
        for (const tableName of activeTables) {
            // if the report's table list includes this active table, then check for Search Criteria against required data
            if (report.tables.indexOf(tableName) > -1) {
                // check if temp data array is empty. If so, the report only needs list of ID's, always display.
                if (tempReportData.length === 0 && this.reportsToShow.indexOf(report) < 0) {
                    this.reportsToShow.push(report);
                    break;
                }

                this.setupReportsToShowForSearchCriteria(report, tempReportData);

                // on to the next report (exit the inner for loop)
                break;
            }
        }
    }

    setupReportsToShowForSearchCriteria(report: IReport, tempReportData: any[]) {
        // loop through tempData array containing region types
        if (this.searchCriteriaRegion) {
            for (const regionType of tempReportData) {
                for (const key of this.searchCriteriaRegion) {
                    key.type = key.type === 'municipality' ? 'countyMunicipalityCode' : key.type;
                    if (regionType === key.type && this.reportsToShow.indexOf(report) < 0) {
                        this.reportsToShow.push(report);
                        break;
                    }
                }
            }
        }
    }

    generateReport(report: IReport, isCsv?: boolean) {
        if (isCsv) {
            this.loading = { status: true, text: 'Generating CSV...' };

            const reportParameters = this.reportData;
            reportParameters.baseUrl = environmentOm.oneMapURL;
            reportParameters.reportName = report.name;

            this.reportsApi.generateCSV(reportParameters)
                .then(response => {
                    const body = 'body';
                    const title = 'title';
                    const file = new Blob([response[body]], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(file);
                    const link = document.createElement('a');
                    link.setAttribute('href', url);
                    link.setAttribute('download', `${response[title]}.csv`);
                    link.style.display = 'none';
                    link.click();
                    window.URL.revokeObjectURL(url);

                    // NOTE: browser must have setting "Ask where to save each file before downloading" set in order for 'Save As' dialog to appear

                    this.loading.status = false;
                }, (error) => {
                    this.loading.status = false;
                    throw error;
                });
        } else {
            this.reportLauncher.generateReport(report.name, this.reportData);
        }
    }

    async loadData() {
        const data: { [type: string]: any } = {};
        await this.reportHelper.getIds('PROJECT_ID', this.reportHelper.tableNames.mpms).then((ids) => {
            data.projectIds = ids;
        });
        await this.reportHelper.getIds('BRKEY', this.reportHelper.tableNames.bridges).then((ids) => {
            data.bridgeKeys = ids;
        });

        if (this.searchCriteriaRegion) {
            for (const region of this.searchCriteriaRegion) {
                data.countyNumber = region.countyCode;
                data.districtNumber = region.districtNumber;
                data.congressDistrictNumber = region.congressDistrictNumber;
                data.senateDistrictNumber = region.senateDistrictNumber;
                data.houseDistrictNumber = region.houseDistrictNumber;
                data.planningPartnerShortName = region.planningPartnerShortName;
                data.municipalityId = region.municipalityCode;

                // if there is a countyMunicipalityCode, retrieve the municipality ID value
                if (region.municipalityCode) {
                    data.countyMunicipalityCode = String(data.countyNumber) + String(data.municipalityId);
                }
            }
        }
        return data;
    }

    // getting total number of SD and Draft projects available in selected AOI to display in report list.
    public getProjectTotals(data: any) {
        const ids = {
            bridgeKeys: data.bridgeKeys,
            projectIds: data.projectIds
        };
        return this.reportsApi.getProjectTotals(ids).then((returnData: any) => {
            const totals = {
                draftProjects: returnData && returnData.draftTipTotal ? parseFloat(returnData.draftTipTotal) : 0,
                sdBridges: returnData && returnData.sdBridgeTotal ? parseFloat(returnData.sdBridgeTotal) : 0,
                tipProjects: returnData && returnData.tipTotal ? parseFloat(returnData.tipTotal) : 0,
                totalProjects: ids && ids.projectIds ? ids.projectIds.length : 0,
                totalBridges: ids && ids.bridgeKeys ? ids.bridgeKeys.length : 0
            };
            this.projectTotals = totals;
            // set report disabled flags
            for (const report of this.reports) {
                switch (report.reportType) {
                    case 'projects':
                        report.disabled = !totals.totalProjects;
                        break;
                    case 'tip':
                        report.disabled = !totals.tipProjects;
                        break;
                    case 'draft':
                        report.disabled = !totals.draftProjects;
                        break;
                    case 'sd':
                        report.disabled = !totals.sdBridges;
                        break;
                    case 'bridges':
                        report.disabled = !totals.totalBridges;
                        break;
                    default:
                        break;
                }

            }
        });
    }
}

export type ReportDataType = 'projectIds' | 'bridgeKeys' | 'county' | 'district' | 'countyMunicipalityCode' | 'congressDistrict' | 'senateDistrict' | 'houseDistrict' | 'planningPartner' | 'reportName' | 'baseUrl';
export interface IReport {
    name: string;
    title: string;
    reportType?: string;
    data?: ReportDataType[];
    tables?: string[];
    disabled?: boolean;
    csvPrint?: boolean;
    selector?: string;
    inputs?: { [key: string]: string };
    component?: string;
}
interface ICsvReport {
    name: string;
    data: ReportDataType[];
}
